import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { CircleCheckBigIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useListProjects, useStartAssessment } from '@/api/assessments.hook'
import { useGetCompany } from '@/api/company.hook'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { getAssessmentName } from '@/lib/assessment/assessment-name'
import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import { cn } from '@/lib/ui/style-helpers'

import { useCompanyHasAssessments } from '@/pages/assessment/ag-grid-assessment/config/use-company-has-assessments'
import { POST_ASSIGN_LABEL } from '@/pages/company-drawer/company-overview/post-assign-assessments-modal/labels'

import { Card } from '@/components/lemmala-design-system/card/card'
import { Checkbox } from '@/components/lemmala-design-system/checkbox/checkbox'
import { Modal } from '@/components/lemmala-design-system/modal/modal'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'

export type PostAssignAssessmentsModalProps = {
  companyId: string
  riskLevel: RiskLevel
  onClose: () => void
}

export const PostAssignAssessmentsModal = ({
  companyId,
  riskLevel,
  onClose,
}: PostAssignAssessmentsModalProps) => {
  const { toast } = useToast()
  const reportErrors = useReportErrorsCallback()
  const { projects } = useListProjects()
  const { hasOngoingAssessmentWithProject } = useCompanyHasAssessments(companyId)
  const { mutateAsync: startAssessment, isPending } = useStartAssessment(companyId)
  const { data: companyData } = useGetCompany(companyId)

  const companyName = companyData?.company?.profile?.name ?? ''

  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<string>>(new Set())
  const createdAtPreview = useMemo(() => timestampFromDate(new Date()), [])

  const toggleProject = (projectId: string) =>
    setSelectedProjectIds((prev) => {
      const next = new Set(prev)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })

  const handleAddAssessments = async () => {
    if (selectedProjectIds.size === 0) {
      toast({ variant: 'destructive', title: POST_ASSIGN_LABEL.ERROR_NO_PROJECT_SELECTED })
      return
    }
    try {
      await Promise.all(
        Array.from(selectedProjectIds).map((projectId) =>
          startAssessment({ companyId, projectId, startAsInProgress: true }),
        ),
      )
      onClose()
    } catch (error) {
      reportErrors(error as Error)
      toast({ variant: 'destructive', title: POST_ASSIGN_LABEL.ERROR_CREATE_FAILED })
    }
  }

  return (
    <Modal
      open
      size='medium'
      title={companyName}
      onDismiss={onClose}
      onCancelButton={onClose}
      onOk={handleAddAssessments}
      primaryButtonText={POST_ASSIGN_LABEL.ADD_ASSESSMENTS}
      primaryButtonProps={{
        disabled: isPending || selectedProjectIds.size === 0,
      }}
    >
      <div className='flex flex-col gap-8'>
        <Card className='flex flex-col items-start gap-2 p-4'>
          <div className='flex items-center gap-2.5'>
            <CircleCheckBigIcon className='size-5 text-main-foreground-success' />
            <span className='text-main-foreground-primary text-heading-small'>
              {POST_ASSIGN_LABEL.INHERENT_RISK_SET_TO}
            </span>
          </div>
          <RiskBadge riskLevel={riskLevel} size='large' />
        </Card>

        <div className='flex flex-col gap-3'>
          <p className='text-main-foreground-tertiary text-label-default'>
            {POST_ASSIGN_LABEL.NEXT_STEP}
          </p>
          <div className='flex flex-col gap-1'>
            <p className='text-main-foreground-primary text-heading-xsmall'>
              {POST_ASSIGN_LABEL.SELECT_PROJECTS_TITLE}
            </p>
            <p className='text-main-foreground-primary text-body-default'>
              {POST_ASSIGN_LABEL.SELECT_PROJECTS_SUBTITLE}
            </p>
          </div>

          <div className='flex flex-col gap-2 pb-2'>
            {projects.map((project) => {
              const assessmentExists = hasOngoingAssessmentWithProject(project.id)
              const checkboxId = `post-assign-project-${project.id}`
              return (
                <label
                  key={project.id}
                  htmlFor={checkboxId}
                  className={cn(
                    'flex w-full items-center justify-between gap-8 rounded border border-main-border-default p-4 shadow-sm',
                    assessmentExists
                      ? 'cursor-not-allowed bg-main-background-muted'
                      : 'cursor-pointer bg-main-background-card hover:bg-main-background-muted',
                  )}
                >
                  <div className='flex items-center gap-1.5'>
                    <Checkbox
                      id={checkboxId}
                      disabled={assessmentExists}
                      checked={selectedProjectIds.has(project.id)}
                      onCheckedChange={() => toggleProject(project.id)}
                    />
                    <span className='text-main-foreground-primary text-body-default'>
                      {project.name}
                    </span>
                  </div>
                  <span className='text-main-foreground-tertiary text-body-default'>
                    {getAssessmentName({
                      orgCompanyName: companyName,
                      projectName: project.name,
                      createdAt: createdAtPreview,
                    })}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      </div>
    </Modal>
  )
}
